import { cn } from '../../cn';
import type { Column, RowData } from '@tanstack/react-table';
import { useResourceKit } from '@resource-kit/core/react';

interface DataTableColumnHeaderProps<TData extends RowData> {
  column: Column<TData, unknown>;
  title: string;
  className?: string;
}

export function DataTableColumnHeader<TData extends RowData>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData>) {
  const { t } = useResourceKit();
  const label = t(title, { defaultValue: title });
  if (!column.getCanSort()) {
    return (
      <div
        className={cn(
          'text-xs font-medium text-muted-foreground uppercase tracking-wider',
          className,
        )}
      >
        {label}
      </div>
    );
  }

  const sorted = column.getIsSorted();

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <button
        type="button"
        onClick={() => column.toggleSorting(sorted === 'asc')}
        className="flex items-center space-x-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground focus:outline-none"
      >
        <span>{label}</span>
        <span className="ml-1 flex h-4 w-4 items-center justify-center">
          {sorted === 'desc' ? (
            <span className="text-foreground">↓</span>
          ) : sorted === 'asc' ? (
            <span className="text-foreground">↑</span>
          ) : (
            <span className="text-muted-foreground/50 opacity-0 group-hover:opacity-100">⇅</span>
          )}
        </span>
      </button>
    </div>
  );
}
